import A11yMenu from './_a11yMenu';
import Splide from '@splidejs/splide';
import GLightbox from 'glightbox';

import {
	OverlayScrollbars,
	ScrollbarsHidingPlugin,
	ClickScrollPlugin,
	SizeObserverPlugin,
} from 'overlayscrollbars';

const initScrollbars = () => {
	const scrolls = document.querySelectorAll('.js-scrollbar');
	if (scrolls) {
		OverlayScrollbars.plugin([SizeObserverPlugin, ScrollbarsHidingPlugin, ClickScrollPlugin]);
		scrolls.forEach((elt) => {
			OverlayScrollbars(elt, {
				overflow: {
					y: 'hidden',
				},
				scrollbars: {
					theme: 'os-theme-dark',
					visibility: 'auto',
					clickScroll: true,
				},
			}, {
				updated(osInstance) {
					// For accessibility

					const scrollableDiv = osInstance.elements().content;
					if (osInstance.state().hasOverflow.x) {
						scrollableDiv.setAttribute('tabindex', 0);
						scrollableDiv.setAttribute('role', 'region');
						scrollableDiv.setAttribute('aria-label', 'Contenu avec scroll');
					} else {
						scrollableDiv.setAttribute('tabindex', -1);
						scrollableDiv.removeAttribute('role');
						scrollableDiv.removeAttribute('aria-label');
					}
				},
			});
		});
	}
};

const initMultiblocksSplides = () => {
	const multiblocksSplides = document.querySelectorAll('.multiblocks-splide');
	multiblocksSplides.forEach((elt) => {
		const splide = new Splide(elt, {
			pagination: false,
			gap: elt.dataset.gap,
			mediaQuery: 'min',
			breakpoints: {
				1400: {
					destroy: true,
				},
				1280: {
					perPage: 3,
					padding: 0,
				},
				1024: {
					perPage: 2,
					padding: { right: '10rem' },
				},
				920: {
					perPage: 2,
					padding: { right: '0' },
				},
				780: {
					perPage: 1,
					padding: { right: '20rem' },
				},
				640: {
					perPage: 1,
					padding: { right: '10rem' },
				},
				100: {
					perPage: 1,
				},
			},
		});
		splide.mount();
	});
};

const initReviewsSplides = () => {
	const reviewsSplides = document.querySelectorAll('.reviews-splide');
	reviewsSplides.forEach((elt) => {
		const splide = new Splide(elt, {
			pagination: false,
			gap: '20px',
			perPage: 4,
			padding: { right: '10rem' },
			breakpoints: {
				1480: {
					perPage: 3,
				},
				1200: {
					perPage: 2,
				},
				900: {
					perPage: 2,
					padding: { right: '8rem' },
				},
				720: {
					perPage: 1,
				},
				480: {
					perPage: 1,
					padding: { right: '5rem' },
				},
				400: {
					perPage: 1,
					padding: { right: '2rem' },
				},
			},
		});
		const bar = splide.root.querySelector('.splide__progress-bar');

		// Updates the bar width whenever the carousel moves:
		splide.on('mounted move', () => {
			const end = splide.Components.Controller.getEnd() + 1;
			const rate = Math.min((splide.index + 1) / end, 1);
			bar.style.width = `${String(100 * rate)}%`;
		});

		splide.mount();
	});
};

const initGallerySplides = () => {
	const gallerySplides = document.querySelectorAll('.gallery-splide');
	gallerySplides.forEach((elt) => {
		const splide = new Splide(elt, {
			pagination: false,
			gap: '40px',
			perPage: 2,
			breakpoints: {
				640: {
					perPage: 1,
					autoWidth: false,
				},
			},
		});
		const bar = splide.root.querySelector('.splide__progress-bar');

		// Updates the bar width whenever the carousel moves:
		splide.on('mounted move', () => {
			const end = splide.Components.Controller.getEnd() + 1;
			const rate = Math.min((splide.index + 1) / end, 1);
			bar.style.width = `${String(100 * rate)}%`;
		});

		splide.mount();
	});
};

const initGalleryGridSplides = () => {
	const galleryGridSplides = document.querySelectorAll('.gallery-grid-splide');
	galleryGridSplides.forEach((elt) => {
		const splide = new Splide(elt, {
			pagination: false,
			gap: '40px',
			mediaQuery: 'min',
			breakpoints: {
				1200: {
					destroy: true,
				},
				1024: {
					perPage: 3,
					gap: '40px',
				},
				480: {
					perPage: 2,
					gap: '20px',
					padding: { right: '0' },
				},
				100: {
					perPage: 1,
					gap: '20px',
					padding: { right: '5rem' },
				},
			},
		});
		const bar = splide.root.querySelector('.splide__progress-bar');

		// Updates the bar width whenever the carousel moves:
		splide.on('mounted move', () => {
			const end = splide.Components.Controller.getEnd() + 1;
			const rate = Math.min((splide.index + 1) / end, 1);
			bar.style.width = `${String(100 * rate)}%`;
		});

		splide.mount();
	});
};

const initGalleryLogosSplides = () => {
	const galleryLogosplides = document.querySelectorAll('.gallery-logos');
	galleryLogosplides.forEach((elt) => {
		const splide = new Splide(elt, {
			pagination: false,
			gap: '40px',
			mediaQuery: 'min',
			breakpoints: {
				1000: {
					destroy: true,
				},
				400: {
					perPage: 3,
					gap: '20px',
				},
				100: {
					perPage: 2,
					gap: '20px',
				},
			},
		});

		splide.mount();
	});
};

const initContentSplides = () => {
	const contentsplides = document.querySelectorAll('.content-splide');
	contentsplides.forEach((elt) => {
		const splide = new Splide(elt, {
			pagination: false,
			mediaQuery: 'min',
			perPage: 3,
			breakpoints: {
				1000: {
					destroy: true,
				},
				999: {
					perPage: 3,
					gap: '40px',
				},
				720: {
					perPage: 2,
					gap: '40px',
				},
				100: {
					perPage: 1,
					gap: '40px',
				},
			},
		});

		splide.mount();
	});
};

const initHeaderSplides = () => {
	const headerSplide = document.querySelectorAll('.header-splide');
	headerSplide.forEach((elt) => {
		const splide = new Splide(elt, {
			pagination: false,
			gap: '40px',
			perPage: 1,
		});

		splide.mount();
	});
};

const loadMore = () => {
	const loadMoreButton = document.querySelector('#js-load-more');

	if (!loadMoreButton) return false;

	const status = document.getElementById('js-status');
	const parent = loadMoreButton.parentElement.parentElement;

	if (loadMoreButton) {
		let isLoading = false;
		loadMoreButton.addEventListener('click', () => {
			if (isLoading) {
				return false;
			}

			isLoading = true;

			let lastPost = parent.querySelectorAll('.card-wrapper');
			lastPost = lastPost[lastPost.length - 1];

			const types = loadMoreButton.dataset.posttypes;
			const cats = loadMoreButton.dataset.cats;
			const ajaxurl = my_ajax_object.ajax_url;
			const nonce = my_ajax_object.nonce;
			const maxPage = loadMoreButton.dataset.max;
			const page = loadMoreButton.dataset.page;

			const data = {
				action: 'load_more',
				nonce,
				types,
				cats,
				page,
			};

			status.innerHTML = 'Chargement en cours.';

			fetch(ajaxurl, {
				method: 'POST',
				body: new URLSearchParams(data),
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
					'Cache-Control': 'no-cache',
				},
			})
				.then(response => response.json())
				.then(body => {
					if (body.success && body.data.html.length > 0) {
						loadMoreButton.dataset.page = body.data.page;

						lastPost.insertAdjacentHTML('afterend', body.data.html);

						parent.querySelector('#first-new-post').scrollIntoView();
						parent.querySelector('#first-new-post').focus();
						parent.querySelector('#first-new-post').removeAttribute('id');

						if (loadMoreButton.dataset.page === maxPage) {
							loadMoreButton.parentElement.remove();
						}
						status.innerHTML = '';
					} else {
						status.innerHTML = '';
						status.innerHTML = `Les articles n'ont pas pu être chargés.`;
					}
					isLoading = false;
				})
				.catch(() => {
					isLoading = false;

					status.innerHTML = '';
					status.innerHTML = 'Une erreur a eu lieu lors du chargement.';
				});

			return true;
		});
	}
	return true;
};

const shareAction = (button) => {
	const href = button.target.dataset.href;
	const service = button.target.dataset.service;

	if (service !== 'mail') {
		window.open(href, '_blank');
	} else {
		window.location.href = href;
	}
};

document.addEventListener('DOMContentLoaded', () => {
	// Solve menu animation firing on page load
	const trItem = document.querySelector('.preload-animation');
	if (trItem) {
		window.setTimeout(() => {
			trItem.classList.remove('preload-animation');
		}, 640);
	}

	const menu = new A11yMenu(document.querySelector('.a11y-nav-block'), '80rem');
	menu.menuSetup();

	initScrollbars();

	initMultiblocksSplides();

	initReviewsSplides();

	initGallerySplides();

	initGalleryGridSplides();

	initGalleryLogosSplides();

	initContentSplides();

	initHeaderSplides();

	loadMore();

	const lightbox = GLightbox({
		touchNavigation: true,
		loop: true,
	});

	const shareButtons = document.querySelectorAll('.share-button');
	shareButtons.forEach(element => {
		element.addEventListener('click', shareAction, false);
	});
});
